import { Crew, Doll, Ensign, Nation, Permoflages, Vehicle } from '~/vortex';
import { NAME_BACKGROUND_FILTER, NAME_PATCH_FILTER, NAME_SYMBOL_FILTER } from '~/Actions/ActionFilter';
import { CREWS_CATEGORY, DOLLS_CATEGORY, ENSIGNS_CATEGORY, PERMOFLAGES_CATEGORY, VEHICLE_CATEGORY } from '~/Actions/ActionCategory';
import { categoriesSettings, getArrayFromObjectByKey } from '~/helpers/utils';
import { DOLLS_TYPES, INVENTORY_KEY_BY_CATEGORY, TAGS, TYPE_IS_RECEIVED, TYPE_IS_NOT_RECEIVED } from '~/helpers/consts';
import { prepareCrews } from '~/settings/response/crews';
import ResponseItemMap from './response/index';
import { getDefaultDogTagComponents, prepareDolls } from '~/settings/response/dolls';
import { prepareEnsigns } from '~/settings/response/ensigns';
import { _Item, ICategories, IInventory, IInventoryList, IInventoryState, IItemFilters, IResponse, Item, VortexItem } from '~/interfaces';
import {
    CREW_CATEGORY_NAME_FILTER,
    CREW_TYPE_NAME_FILTER,
    DOLL_CATEGORY_NAME_FILTER,
    ENSIGN_CATEGORY_NAME_FILTER,
    LEVEL_SHIP_NAME_FILTER,
    NATION_NAME_FILTER,
    RARITY_NAME_FILTER,
    TYPE_SHIP_NAME_FILTER,
} from '~/Components/Filter/filtersMap';
import { getCustomNations } from '@wg/wows-entities';

export const prepareResponseFromFilters = (response: IResponse, inventory: IInventoryState): IResponse => {
    Object.keys(response).forEach((key: ICategories) => {
        if (!INVENTORY_KEY_BY_CATEGORY[key]) {
            return;
        }

        const items = response[key] as VortexItem[];
        const inventoryKey = INVENTORY_KEY_BY_CATEGORY[key] as IInventoryList;
        const inventoryItems = (inventory?.[inventoryKey] || []) as string[];

        items.forEach((item: _Item) => {
            const vortexItemData = item as VortexItem;
            const filters: IItemFilters = {};

            if (key === VEHICLE_CATEGORY) {
                const ship = vortexItemData as Vehicle;

                filters[LEVEL_SHIP_NAME_FILTER] = [ship.level.toString()];
                filters[NATION_NAME_FILTER] = [ship.nation.name];
                filters[TYPE_SHIP_NAME_FILTER] = [ship.type.name];
                filters[RARITY_NAME_FILTER] = ship.rarity ? [ship.rarity] : [];
            } else if (key === CREWS_CATEGORY) {
                const crew = vortexItemData as Crew;

                filters[NATION_NAME_FILTER] = [crew.nation.name];
                filters[CREW_CATEGORY_NAME_FILTER] = [crew.category];
                filters[CREW_TYPE_NAME_FILTER] = [crew.type];
            } else if (key === PERMOFLAGES_CATEGORY) {
                const camo = vortexItemData as Permoflages;
                const ship = camo.vehicles[0]?.vehicle;

                filters[LEVEL_SHIP_NAME_FILTER] = [ship?.level.toString()];
                filters[NATION_NAME_FILTER] = [ship?.nation.name];
                filters[TYPE_SHIP_NAME_FILTER] = [ship?.type.name];
                filters[RARITY_NAME_FILTER] = camo.rarity ? [camo.rarity] : [];
            } else if (key === ENSIGNS_CATEGORY) {
                const ensign = vortexItemData as Ensign;

                filters[ENSIGN_CATEGORY_NAME_FILTER] = [ensign.categoryType];
                filters[RARITY_NAME_FILTER] = ensign.rarity ? [ensign.rarity] : [];
            } else if (key === DOLLS_CATEGORY) {
                const doll = vortexItemData as Doll;
                let type = NAME_BACKGROUND_FILTER;
                if (doll.type === DOLLS_TYPES.PATCH) {
                    type = NAME_PATCH_FILTER;
                } else if (doll.type === DOLLS_TYPES.SYMBOL) {
                    type = NAME_SYMBOL_FILTER;
                }

                filters[DOLL_CATEGORY_NAME_FILTER] = [type];
                filters[RARITY_NAME_FILTER] = doll.rarity ? [doll.rarity] : [];
            }

            item.filters = {
                display: inventoryItems?.includes(vortexItemData.id) ? [TYPE_IS_RECEIVED] : [TYPE_IS_NOT_RECEIVED],
                ...filters,
            };
        });
    });

    return response;
};

export const prepareResponse = (response: IResponse, inventory: IInventory): IResponse => {
    const availableVehicleClass = new Set();
    const availableVehicleNations = new Set();
    const availableVehicleLevels = new Set();
    const rareVehicles: Vehicle[] = [];

    response.vehicles = response.vehicles
        .map((vehicle: Vehicle & _Item) => {
            if (ResponseItemMap.isUniqueItem(VEHICLE_CATEGORY, vehicle)) {
                vehicle.isUniqueItem = true;
            }

            // todo позже появится у них тег аниме
            // todo 11 февраля 16:58 - этот день еще пока не настал, но народ не теряет надежды
            // todo 12.06.2025 (4 года спустя) Мы все еще не достойны
            if (ResponseItemMap.getHardcodeAnimeShipIds().includes(vehicle.id)) {
                vehicle.tags.push(TAGS.HSF);
            }

            availableVehicleClass.add(vehicle.type.name.toLowerCase());
            availableVehicleNations.add(vehicle.nation.name.toLowerCase());
            availableVehicleLevels.add(vehicle.level);

            return vehicle;
        })
        .filter((vehicle) => {
            return !ResponseItemMap.getExcludedShipIds().includes(vehicle.id);
        });

    response.vehicles = [...rareVehicles, ...response.vehicles];
    response.vehicleTypes = response.vehicleTypes.filter((item) => availableVehicleClass.has(item.name.toLowerCase()));
    response.nations = [
        ...response.nations.filter((item: Nation) => availableVehicleNations.has(item.name.toLowerCase())),
        ...Object.values(getCustomNations()),
    ];

    response.crews = prepareCrews(response.crews);
    response.dogTagComponents = prepareDolls(response.dogTagComponents);
    response.ensigns = prepareEnsigns(response.ensigns);
    response.defaultDogTagComponents = getDefaultDogTagComponents(response.dogTagComponents);

    const inventoryHoldsIds = getArrayFromObjectByKey(inventory.hold || [], 'id');
    const vehiclesHoldsData = (inventory?.hold || []).reduce((state: any, item) => {
        state[item.id] = item;
        return state;
    }, {});

    response.permoflages = response.permoflages
        .reduce((array, item) => {
            item.vehicles = item.vehicles.filter((vehicle) => {
                return !vehicle.vehicle.tags.includes(TAGS.PRESERVED);
            });

            if (item.vehicles.length > 1) {
                const copyItem = { ...item };
                item.vehicles.forEach((i, index) => {
                    const li = { ...copyItem };
                    li.vehicles = li.vehicles.filter((listItem, listIndex) => listIndex === index);

                    if (!inventoryHoldsIds.includes(item.id)) {
                        array.push(li);
                    } else {
                        const availableVehicles = (vehiclesHoldsData[item.id] || {}).vehicles || [];
                        const arrayVehiclesIds = getArrayFromObjectByKey(availableVehicles, 'vehicleId');
                        if (arrayVehiclesIds.includes(i.vehicle?.id.toString())) {
                            array.push(li);
                        }
                    }
                });
            } else {
                array.push(item);
            }

            return array;
        }, [])
        .sort((a, b) => {
            if (a.title < b.title) {
                return -1;
            } else if (a.title > b.title) {
                return 1;
            }

            return 0;
        });

    response.permoflages = response.permoflages
        .map((item: Permoflages & _Item) => {
            if (ResponseItemMap.isUniqueItem(PERMOFLAGES_CATEGORY, item)) {
                item.isUniqueItem = true;
            }

            return item;
        })
        .filter((item) => {
            if (ResponseItemMap.getExcludedPermoflagesIds().includes(item.id)) {
                return false;
            }

            if (item.vehicles[0]?.vehicle?.tags.includes(TAGS.DEMO_WITHOUT_STATS)) {
                return false;
            }

            return true;
        });

    return response;
};

export const postPrepareResponse = (
    response: IResponse,
    inventory: IInventoryState,
): {
    response: IResponse;
    inventory: IInventoryState;
} => {
    const vehiclesInfo = {
        nations: new Set(),
        types: new Set(),
        levels: new Set(),
    };

    const _isFeatsUniqueItem = (category: ICategories, item: Item) => {
        const inventoryKey = INVENTORY_KEY_BY_CATEGORY[category] as IInventoryList;
        const inventoryItems = (inventory?.[inventoryKey] || []) as string[];

        return !(item.isUniqueItem && !inventoryItems.includes(item.id.toString())) || !item.isUniqueItem;
    };

    response.vehicles = response.vehicles.filter((item: Vehicle & _Item) => {
        const isFeats = _isFeatsUniqueItem(VEHICLE_CATEGORY, item);

        if (isFeats) {
            vehiclesInfo.nations.add(item.nation.name);
            vehiclesInfo.types.add(item.type.name);
            vehiclesInfo.levels.add(item.level);
        }

        return isFeats;
    });

    // @ts-ignore
    response.vehiclesInfo = vehiclesInfo;

    response.ensigns = response.ensigns.filter((item: Ensign & _Item) => {
        const isFeats = _isFeatsUniqueItem(ENSIGNS_CATEGORY, item);

        return isFeats;
    });

    response.dogTagComponents = response.dogTagComponents.filter((item: Doll & _Item) => {
        const isFeats = _isFeatsUniqueItem(DOLLS_CATEGORY, item);

        const isCorrectType = [DOLLS_TYPES.PATCH, DOLLS_TYPES.SYMBOL, DOLLS_TYPES.SUBSTRATE, DOLLS_TYPES.BACKGROUND].includes(item.type);

        return isFeats && isCorrectType;
    });

    const permoflagesVehicleInfo = {
        nations: new Set(),
        types: new Set(),
        levels: new Set(),
    };

    const includeNativeForced = categoriesSettings.permoflages?.includeNativeForced || [];
    response.permoflages = response.permoflages.filter((item: Permoflages & _Item) => {
        const itemId = item.id.toString();
        let isFeats = true;
        if (!item.vehicles.length) {
            isFeats = false;
        } else if (ResponseItemMap.getExcludedPermoflagesIds().includes(itemId)) {
            isFeats = false;
        } else if (item.isUniqueItem && !inventory?.hold?.includes(itemId)) {
            isFeats = false;
        } else if (item.vehicles[0] && item.vehicles[0].isNative && !includeNativeForced.includes(itemId)) {
            isFeats = false;
        } else if (!inventory) {
            isFeats = !item.isUniqueItem;
        }

        const vehicle = item.vehicles[0]?.vehicle || {};

        if (isFeats) {
            permoflagesVehicleInfo.nations.add(vehicle.nation.name);
            permoflagesVehicleInfo.types.add(vehicle.type.name);
            permoflagesVehicleInfo.levels.add(vehicle.level);
        }

        return isFeats;
    });

    // @ts-ignore
    response.permoflagesVehiclesData = permoflagesVehicleInfo;

    response.crews = response.crews.filter((crew: Crew & _Item) => {
        const isFeats = _isFeatsUniqueItem(CREWS_CATEGORY, crew);

        return isFeats;
    });

    const crewsNations = response.crews.reduce((Set, item) => Set.add(item.nation.name) && Set, new Set());
    // @ts-ignore
    response.crewsNations = response.nations.filter((item) => crewsNations.has(item.name));

    const crewsInfo = {
        nations: crewsNations,
    };

    // @ts-ignore
    response.crewsInfo = crewsInfo;

    return {
        response,
        inventory,
    };
};
